import React from 'react';
import { Search, ViewModule, CropDin } from '@mui/icons-material';
import { tagMeta } from '../../core/criteriaTags';
import ButtonGroup from '../utils/ButtonGroup';
import Toggle from '../utils/Toggle';
import MediaImg from '../utils/MediaImg';
import './BuildListHeader.css';

/**
 * Shared toolbar for build lists (MyBuilds + Community): class/tag icon filters,
 * a search field, and an inline "sort by" control. Fully controlled — the parent
 * owns all state (React state on MyBuilds, URL params on Community).
 *
 * Props:
 *  - classes: [{ name, icon, label }]        selectedClasses: Set, onToggleClass(name)
 *  - tagIds: string[]                         selectedTags: Set, onToggleTag(id)
 *  - title: node rendered at the row start (section heading / tabs); switches the
 *    toolbar to "title left / count + sort + search right"
 *  - searchValue, onSearchChange, searchPlaceholder   (search hidden when no onSearchChange)
 *  - sortOptions: [{ value, label }], sortValue, onSortChange(value)
 *  - sortLabel: aria-only label for the sort ButtonGroup (not shown)
 *  - viewMode: 'compact' | 'full', onViewModeChange(value), viewModeLabel
 */

const TXT_VIEW_COMPACT = {
    fr: 'Vue compacte',
    en: 'Compact view',
    de: 'Kompakte Ansicht',
    es: 'Vista compacta',
    pt: 'Vista compacta',
};
const TXT_VIEW_FULL = {
    fr: 'Vue détaillée',
    en: 'Full view',
    de: 'Detailansicht',
    es: 'Vista completa',
    pt: 'Vista completa',
};
const TXT_VIEW_MODE = {
    fr: 'Mode d\'affichage',
    en: 'Display mode',
    de: 'Anzeigemodus',
    es: 'Modo de visualización',
    pt: 'Modo de exibição',
};

function getTxt(obj, lang) {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] || obj.en || obj.fr || '';
}

const BuildListHeader = ({
    lang = 'fr',
    title = null,
    classes = [],
    selectedClasses,
    onToggleClass,
    tagIds = [],
    selectedTags,
    onToggleTag,
    searchValue = '',
    onSearchChange,
    searchPlaceholder = '',
    leading = null,
    sortOptions = [],
    sortValue,
    onSortChange,
    sortLabel,
    viewMode,
    onViewModeChange,
    viewModeLabel,
}) => {
    const hasClassFilters = classes.length > 0 && onToggleClass;
    const hasTagFilters = tagIds.length > 0 && onToggleTag;
    const hasSort = sortOptions.length > 0 && onSortChange;
    const hasViewMode = !!onViewModeChange && (viewMode === 'compact' || viewMode === 'full');

    return (
        <div className="build-list-header">
            <div className={`build-list-toolbar${title ? ' build-list-toolbar--with-title' : ''}`}>
                {title && <div className="build-list-toolbar-title">{title}</div>}
                {leading && <div className="build-list-toolbar-leading">{leading}</div>}
                {hasSort && (
                    <div className="build-list-sort">
                        <ButtonGroup
                            className="build-list-sort-group"
                            ariaLabel={sortLabel}
                            items={sortOptions}
                            value={sortValue}
                            onChange={onSortChange}
                        />
                    </div>
                )}
                {onSearchChange && (
                    <div className="build-list-search-wrap">
                        <Search className="build-list-search-icon" aria-hidden="true" />
                        <input
                            className="build-list-search"
                            type="search"
                            value={searchValue}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            aria-label={searchPlaceholder || undefined}
                        />
                    </div>
                )}
                {hasViewMode && (
                    <Toggle
                        variant="pill"
                        className="build-list-view-toggle"
                        ariaLabel={viewModeLabel || getTxt(TXT_VIEW_MODE, lang)}
                        value={viewMode}
                        onChange={onViewModeChange}
                        items={[
                            {
                                value: 'compact',
                                icon: <ViewModule fontSize="small" />,
                                tooltip: getTxt(TXT_VIEW_COMPACT, lang),
                                ariaLabel: getTxt(TXT_VIEW_COMPACT, lang),
                            },
                            {
                                value: 'full',
                                icon: <CropDin fontSize="small" />,
                                tooltip: getTxt(TXT_VIEW_FULL, lang),
                                ariaLabel: getTxt(TXT_VIEW_FULL, lang),
                            },
                        ]}
                    />
                )}
            </div>

            {(hasClassFilters || hasTagFilters) && (
                <div className="mybuilds-filters">
                    {hasClassFilters && (
                        <div className="mybuilds-filter-group">
                            {classes.map(cls => (
                                <button
                                    key={cls.name}
                                    type="button"
                                    className={`mybuilds-filter-btn ${selectedClasses?.has(cls.name) ? 'active' : ''}`}
                                    onClick={() => onToggleClass(cls.name)}
                                    title={getTxt(cls.label, lang)}
                                >
                                    <MediaImg
                                        className="mybuilds-filter-img mybuilds-filter-img--class"
                                        src={cls.icon}
                                        alt={cls.name}
                                        fallback=""
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                    {hasClassFilters && hasTagFilters && (
                        <div className="mybuilds-filter-divider" aria-hidden="true" />
                    )}
                    {hasTagFilters && (
                        <div className="mybuilds-filter-group">
                            {tagIds.map(tagId => {
                                const meta = tagMeta(tagId, lang);
                                if (!meta) return null;
                                return (
                                    <button
                                        key={tagId}
                                        type="button"
                                        className={`mybuilds-filter-btn ${selectedTags?.has(tagId) ? 'active' : ''}`}
                                        onClick={() => onToggleTag(tagId)}
                                        title={meta.label}
                                    >
                                        {meta.icon
                                            ? (
                                                <MediaImg
                                                    className="mybuilds-filter-img mybuilds-filter-img--tag"
                                                    src={meta.icon}
                                                    alt=""
                                                    fallback=""
                                                />
                                            )
                                            : <span className="mybuilds-filter-letter">{meta.label[0]}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BuildListHeader;
